import { Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { filter, pluck } from 'rxjs/operators';
import { AuthService } from 'src/app/root/auth.service';
import { InternalService } from 'src/app/root/internal.service';

@Component({
  selector: 'lohi-app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent {
  public activeLink = '/v2/dashboard';
  public isSidebarOpen = false;
  public isInternal$: Observable<boolean>;
  public carrierName$: Observable<string>;
  public expandedSubmenus: Record<string, boolean> = {};

  public sidebarItems = [
    {
      label: 'Dashboard',
      link: '/v2/dashboard',
      icon: '/assets/icons/new-dashboard/dashboard_icon.svg',
      disabled: false,
      children: [
        {
          label: 'Regional news',
          link: '/v2/regional-news',
          icon: '/assets/icons/new-dashboard/news.svg',
        },
        {
          label: 'Sign-on bonuses',
          link: '/sign-on-bonus',
          icon: '/assets/icons/new-dashboard/billing.svg',
        },
      ],
    },
    {
      label: 'Performance',
      link: '/v2/performance',
      icon: '/assets/icons/new-dashboard/performance_icon.svg',
      disabled: false,
    },
    {
      label: 'Financials',
      link: '/v2/financials',
      icon: '/assets/icons/new-dashboard/financial_icon.svg',
      children: [
        {
          label: 'Billing',
          link: '/v2/loads/list/completed',
          icon: '/assets/icons/new-dashboard/billing.svg',
        },
        {
          label: 'Billing Export',
          link: '/v2/billing-export',
          icon: '/assets/icons/new-dashboard/billing.svg',
        },
      ],
      disabled: true,
    },
    // { label: 'Scheduling', link: '/v2/scheduling', icon: '/assets/icons/new-dashboard/scheduling_icon.svg', disabled: true },
    { label: 'Drivers', link: '/v2/drivers', icon: '/assets/icons/new-dashboard/driver_icon.svg', disabled: true },
    // { label: 'Documents', link: '/v2/documents', icon: '/assets/icons/new-dashboard/documents_icon.svg', disabled: true },
  ];

  public bottomItems = [
    // { label: 'Settings', link: '/v2/settings', icon: '/assets/icons/new-dashboard/settings_icon.svg' },
    // { label: 'Sign Out', link: '/signout', icon: '/assets/icons/new-dashboard/sign_out_icon.svg' },
  ];

  constructor(private router: Router, private authService: AuthService, private internalService: InternalService) {
    this.carrierName$ = this.authService.userInfo$.pipe(pluck('companyName'));
    this.isInternal$ = this.internalService.isInternal$;

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.activeLink = event.urlAfterRedirects;
      this.autoExpandActiveSubmenu();
    });

    let lastCarrierName: string | undefined;

    this.carrierName$.pipe(distinctUntilChanged()).subscribe((newCarrierName) => {
      if (lastCarrierName !== undefined && lastCarrierName !== newCarrierName) {
        window.location.reload();
      }
      lastCarrierName = newCarrierName;
    });
  }

  public toggleSubmenu(parentLabel: string, link: string): void {
    const hasChildren = this.sidebarItems.find((item) => item.label === parentLabel)?.children?.length > 0;

    if (hasChildren) {
      if (this.activeLink !== link) {
        this.activeLink = link;
        this.router.navigate([link]).then(() => {
          Object.keys(this.expandedSubmenus).forEach((key) => {
            if (key !== parentLabel) {
              this.expandedSubmenus[key] = false;
            }
          });

          if (!(parentLabel in this.expandedSubmenus)) {
            this.expandedSubmenus[parentLabel] = false;
          }

          this.expandedSubmenus[parentLabel] = true;
        });
      } else {
        if (!(parentLabel in this.expandedSubmenus)) {
          this.expandedSubmenus[parentLabel] = false;
        }
        this.expandedSubmenus[parentLabel] = !this.expandedSubmenus[parentLabel];
      }
    } else {
      this.activeLink = link;
      this.router.navigateByUrl(link);
    }
  }

  public autoExpandActiveSubmenu(): void {
    this.sidebarItems.forEach((item) => {
      if (
        item.children?.some((child) => this.activeLink === child.link || this.activeLink.startsWith(child.link + '/'))
      ) {
        this.expandedSubmenus[item.label] = true;
      }
    });
  }

  public setActiveLink(link: string, parentLabel?: string): void {
    this.activeLink = link;
    this.router.navigate([link]);

    if (parentLabel) {
      this.expandedSubmenus[parentLabel] = true;
    }
  }

  public isActive(link: string): boolean {
    if (this.activeLink.startsWith('/v2/settlements') && link === '/v2/financials') {
      return true;
    }

    const menuItem = this.sidebarItems.find((item) => item.link === link);
    if (menuItem?.children?.length > 0) {
      const isChildActive = menuItem.children.some(
        (child) => this.activeLink === child.link || this.activeLink.startsWith(child.link + '/'),
      );
      if (isChildActive) {
        return true;
      }
    }

    return this.activeLink === link || this.activeLink.startsWith(link + '/');
  }

  public isChildActive(childLink: string): boolean {
    return this.activeLink === childLink || this.activeLink.startsWith(childLink + '/');
  }

  public trackByLink(index: number, item: { label: string; link: string; icon: string }): string {
    return item.link;
  }

  public logout() {
    this.authService.logout();
  }
}
